/**
 * 1.8.3 (D-1.8.3-5) — volunteer services the office can add. Elijah, 9 Oct 2026: "Maybe I should just add piano to
 * the list of things and check it off under her volunteer card."
 *
 * The six built-in services (shared/volunteers.ts SERVICE_KEYS) stay a closed list and cannot be removed. A service
 * the office adds is **data**: a stable key (a slug of the English name, kept forever), the name in English and in
 * Spanish (the office types both), and whether it is retired. Retiring hides it from new ticks and keeps it readable
 * on every record that has it. Nothing is ever taken off the list.
 *
 * The list is the app's own file (`_app/settings/volunteer-services.json`, Admin → Settings → Companion & Wellness →
 * Volunteers and outings → "Services"); a volunteer's added services are on the volunteer's own file. No SharePoint
 * column, no choice value.
 *
 * Ready for an added service is the waiver only — as Reading, Lessons, Events and Other.
 *
 * Pure rules shared by the API and the app. Nothing here reads or writes a list.
 */
import { z } from 'zod';
import { SERVICE_KEYS, SERVICE_LABEL_EN, type ServiceKey } from './volunteers';

/** Where the list lives (the folder and the file's name). */
export const VOLUNTEER_SERVICES_FOLDER = '_app/settings';
export const VOLUNTEER_SERVICES_FILE = 'volunteer-services.json';

/** How many services the office may add, and how long a name may be. */
export const MAX_ADDED_SERVICES = 40;
export const SERVICE_NAME_MAX = 40;

export interface AddedService {
  /** A slug of the English name, made once and kept forever ("piano", "art-class"). */
  key: string;
  en: string;
  es: string;
  retired: boolean;
}

/** A built-in service's key or an added one's. */
export type AnyServiceKey = ServiceKey | string;

/** The six in Spanish, for words written outside the screens (an email's list of services). The screens use `vol.service.<key>`. */
const BUILT_IN_ES: Record<ServiceKey, string> = { reading: 'Lectura', companion: 'Acompañamiento', wellness: 'Caminatas de bienestar', events: 'Eventos', lessons: 'Clases', other: 'Otro' };

export const isBuiltInService = (key: string): key is ServiceKey => (SERVICE_KEYS as readonly string[]).includes(key);

const clean = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** "Art class!" → "art-class". Letters and digits only, a hyphen between words, at most 40 characters. '' when nothing is left. */
export function slugOfService(en: string): string {
  return fold(clean(en))
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');
}

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const name = z.string().trim().min(1).max(SERVICE_NAME_MAX);

export const AddedServiceSchema = z.object({ key: z.string().regex(SLUG).max(40), en: name, es: name, retired: z.boolean().default(false) });
export const AddedServicesSchema = z.array(AddedServiceSchema).max(MAX_ADDED_SERVICES);

/** What the Settings block sends: the two names. */
export const AddServiceSchema = z.object({ en: name, es: name });
export const RetireServiceSchema = z.object({ retired: z.boolean() });

/** The file's contents as the list (anything unreadable, a built-in's key or a key twice is left out). */
export function parseAddedServices(raw: unknown): AddedService[] {
  const list = Array.isArray(raw) ? raw : [];
  const out: AddedService[] = [];
  for (const x of list) {
    const p = AddedServiceSchema.safeParse(x);
    if (!p.success || isBuiltInService(p.data.key) || out.some((o) => o.key === p.data.key)) continue;
    out.push(p.data);
  }
  return out.slice(0, MAX_ADDED_SERVICES);
}

export type AddRefusal = 'empty' | 'built-in' | 'exists' | 'full';

/** Why a name cannot be added, in the office's words. */
export const ADD_REFUSED_WORDS: Record<AddRefusal, string> = {
  empty: 'Type the name in English and in Spanish.',
  'built-in': 'That is one of the six built-in services already.',
  exists: 'That service is on the list already. If it was retired, bring it back instead.',
  full: `The list is full (${MAX_ADDED_SERVICES} added services). Retire one that is no longer used — its name stays on old records.`,
};

/**
 * The list with one more service at the end. The key is the slug of the English name; a name that is one of the six
 * (by key or by its English label), or already on the list (by key or by either name, retired or not), is refused.
 */
export function withAddedService(list: readonly AddedService[], input: { en: string; es: string }): { list: AddedService[]; service: AddedService } | { refused: AddRefusal } {
  const en = clean(input.en).slice(0, SERVICE_NAME_MAX);
  const es = clean(input.es).slice(0, SERVICE_NAME_MAX);
  const key = slugOfService(en);
  if (!en || !es || !key) return { refused: 'empty' };
  const builtIn = SERVICE_KEYS.some((k) => k === key || fold(SERVICE_LABEL_EN[k]) === fold(en));
  if (builtIn) return { refused: 'built-in' };
  if (list.some((s) => s.key === key || fold(s.en) === fold(en))) return { refused: 'exists' };
  if (list.length >= MAX_ADDED_SERVICES) return { refused: 'full' };
  const service: AddedService = { key, en, es, retired: false };
  return { list: [...list, service], service };
}

/** The list with one service retired (or brought back). Null: no added service has that key — a built-in is never retired. */
export function withRetired(list: readonly AddedService[], key: string, retired: boolean): AddedService[] | null {
  if (isBuiltInService(key) || !list.some((s) => s.key === key)) return null;
  return list.map((s) => (s.key === key ? { ...s, retired } : s));
}

/** The added services a new tick may choose (not retired), in the list's order. */
export const activeServices = (list: readonly AddedService[]): AddedService[] => list.filter((s) => !s.retired);

/**
 * A service in words. A built-in reads by its English label (or its Spanish name); an added one by the name the
 * office typed — retired or not, so an old record still reads; a key the list no longer knows reads as the key.
 */
export function serviceName(key: string, lang: 'en' | 'es', list: readonly AddedService[]): string {
  if (isBuiltInService(key)) return lang === 'es' ? BUILT_IN_ES[key] : SERVICE_LABEL_EN[key];
  const s = list.find((x) => x.key === key);
  return s ? (lang === 'es' ? s.es : s.en) : key;
}

/**
 * The added keys a save may keep: every key the list has that is not retired, and a retired one only when the
 * record had it already (retiring hides it from new ticks; it never takes a tick away). The list's order.
 */
export function settleAdded(want: readonly string[], had: readonly string[], list: readonly AddedService[]): string[] {
  return list.filter((s) => want.includes(s.key) && (!s.retired || had.includes(s.key))).map((s) => s.key);
}

/** The chips and ticks a screen offers: the active ones, and any retired one this record (or this filter) already has. */
export function offeredServices(list: readonly AddedService[], had: readonly string[] = []): AddedService[] {
  return list.filter((s) => !s.retired || had.includes(s.key));
}

/** Does a volunteer have this service ticked (one of the six, or an added one)? */
export function hasService(v: { services: readonly string[]; addedServices?: readonly string[] | null }, key: string): boolean {
  return isBuiltInService(key) ? v.services.includes(key) : (v.addedServices ?? []).includes(key);
}

/** A list of service keys as posted by the card: the six apart from the added ones. */
export function splitServices(keys: readonly string[]): { builtIn: ServiceKey[]; added: string[] } {
  return { builtIn: SERVICE_KEYS.filter((s) => keys.includes(s)), added: [...new Set(keys.filter((k) => !isBuiltInService(k)))] };
}
